import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { updateAppConfigItem } from "../../services/app.service";

const WORLD_CUP_MODE_KEY = "worldCupMode";

const parseWorldCupMode = (value) => value === "true";

const WorldCupModeBlock = ({ value, onSuccess }) => {
  const [enabled, setEnabled] = useState(() => parseWorldCupMode(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setEnabled(parseWorldCupMode(value));
  }, [value]);

  const { mutate, isPending } = useMutation({
    mutationFn: (nextEnabled) =>
      updateAppConfigItem({
        key: WORLD_CUP_MODE_KEY,
        value: nextEnabled ? "true" : "false",
      }),
    onSuccess: () => {
      setError("");
      onSuccess?.();
    },
    onError: (err) => {
      setEnabled(parseWorldCupMode(value));
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to save World Cup mode",
      );
    },
  });

  const handleToggle = useCallback(
    (event) => {
      const nextEnabled = event.target.checked;
      setEnabled(nextEnabled);
      setError("");
      mutate(nextEnabled);
    },
    [mutate],
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        World Cup Mode
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={isPending}
            />
          }
          label={`World Cup ${enabled ? "enabled" : "disabled"}`}
        />
        {isPending ? <CircularProgress size={20} /> : null}
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, maxWidth: 600 }}
      >
        When enabled, mobile users are prompted to pick a nation and can
        generate a World Cup-themed avatar stored separately. When disabled,
        users see their regular team avatar again.
      </Typography>
    </Paper>
  );
};

export default WorldCupModeBlock;
